interface ValidationError {
  type: string;
  loc: string[];
  msg: string;
  input?: unknown;
  ctx?: Record<string, unknown>;
}

interface APIErrorResponse {
  detail?: string | ValidationError[];
  message?: string;
}

export function parseAPIError(error: unknown): string {
  // A response with an empty body (a proxy 500, a gateway timeout) arrives
  // with `data: null`.
  if (error && typeof error === 'object' && 'data' in error && error.data) {
    const errorData = error.data as APIErrorResponse;

    if (Array.isArray(errorData.detail)) {
      const errors = errorData.detail as ValidationError[];

      const errorMessages = errors.map((err) => {
        const field = err.loc
          .filter((l) => l !== 'body')
          .join(' → ')
          .replace(/_/g, ' ');

        if (err.type === 'string_too_short') {
          const minLength = (err.ctx?.min_length as number) || 1;
          return `${capitalizeFirst(field)}: Must be at least ${minLength} character${minLength !== 1 ? 's' : ''}`;
        } else if (err.type === 'missing') {
          return `${capitalizeFirst(field)} is required`;
        } else if (err.type === 'enum') {
          return `${capitalizeFirst(field)}: ${err.msg}`;
        } else {
          return `${capitalizeFirst(field)}: ${err.msg}`;
        }
      });

      return errorMessages.join('. ');
    }

    if (typeof errorData.detail === 'string') {
      return errorData.detail;
    }

    if (errorData.message) {
      return errorData.message;
    }
  }

  if (error && typeof error === 'object' && 'message' in error) {
    return (error as Error).message;
  }

  return 'An unexpected error occurred';
}

/**
 * The response's string `detail`, as-is, or `fallback` for any other shape.
 * For a fuller read of FastAPI validation errors, use `parseAPIError`.
 */
export function errorDetail(error: unknown, fallback: string): string {
  const detail = (error as { data?: { detail?: string } })?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
}

function capitalizeFirst(str: string): string {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}
